/* eslint-disable sort-imports */
/* eslint-disable @typescript-eslint/no-explicit-any */
import './mixins/MaterialTracker';

import {
    colourBandMixin,
    highlightMixin,
    image360Mixin,
    renderMixin,
    utilityMixin,
    viewerMixin,
    workerMixin,
} from './mixins';
import { renderMissingViewerDomHierarchy } from './renderMissingViewerDomHierarchy';
import { PPAppServices } from './services';

import { OPEN_STREET_MAP_PROJECTION, OPEN_STREET_MAP_URL, OSM_ATTRIBUTION_ID } from 'constants/index';
import { VRButton } from '../../libs/three.js/extra/VRButton.js';

// Tracks listeners/observers created by VR UI so they can be cleaned up when the viewer is disposed.
let vrButtonCleanup: (() => void) | null = null;

export interface PootreRenderPreparationsParameters {
    enableMap: boolean;
    projection?: string;
    coordinate_X?: number;
    coordinate_Y?: number;
    coordinate_Z?: number;
}

const { setOsm, setOsmProjection } = PSPCustomization;

const extendPointCloudMaterial = () => {
    // First add mixin methods to prototype
    Object.assign(Potree.PointCloudMaterial.prototype, highlightMixin);
    Object.assign(Potree.PointCloudMaterial.prototype, colourBandMixin);
    Object.assign(Potree.Images360Loader.prototype, image360Mixin);

    // Define new property descriptor for 'material'
    Object.defineProperty(Potree.PointCloudOctree.prototype, 'material', {
        get() {
            return this._material;
        },
        set(value) {
            // Store the material
            this._material = value;

            // If it's a PointCloudMaterial, initialize highlight
            if (value instanceof Potree.PointCloudMaterial) {
                value.initHighlight();
                value.initColourBands();
            }
        },
        configurable: true,
    });
};

export function potreeRenderPreparations(params: PootreRenderPreparationsParameters = { enableMap: false }) {
    const isOSM = params.enableMap || false;
    setOsm(isOSM);
    const viewerDomElement = document.getElementById('potree_render_area') as HTMLElement;
    extendPointCloudMaterial();
    Object.assign(Potree, utilityMixin);
    Object.assign(Potree.Renderer.prototype, renderMixin);
    Object.assign(Potree.Viewer.prototype, viewerMixin);
    Object.assign(Potree.EptLazBatcher.prototype, workerMixin.EptLazBatcher);
    Object.assign(Potree.Images360Loader, image360Mixin.Images360Loader);
    Object.assign(Potree.Images360.prototype, image360Mixin.Images360);

    if (isOSM) {
        const cesiumContainer = document.getElementById('cesiumContainer') as HTMLElement;
        cesiumContainer.style.display = 'block';
        initOSMComponents(viewerDomElement, params);
    } else {
        window.viewer = new Potree.Viewer(viewerDomElement);
        window.viewer.toggleNavigationCube();
        window.viewer.toggleCompass();
    }

    renderMissingViewerDomHierarchy(viewerDomElement);
    // VR button init is intentionally fire-and-forget: we don't await it here so
    // viewer startup is not blocked on WebXR checks and DOM layout. The button
    // appears as soon as the renderer and sidebar layout are ready.
    initVRButton(window.viewer);
    window.pspDataStorage = new PSPCustomization.PspDataStorage();
    window.PPAppServices = new PPAppServices();
}

export async function initVRButton(viewer: any) {
    if (!viewer?.renderer) return;
    if (document.getElementById('VRButton')) return;

    try {
        const vrButton = await VRButton.createButton(viewer.renderer);
        if (!vrButton) return;

        viewer.renderer.xr.enabled = true;

        const elButtons = document.getElementById('potree_quick_buttons');
        if (!elButtons) return;

        const element = vrButton.element;
        let sidebarResizeObserver: ResizeObserver | null = null;

        const computeMenuRight = () => {
            const menu = document.querySelector('section.leftsidebar') as HTMLElement | null;
            if (!menu) return { menu: null, right: 0, ready: false };

            const rectRight = menu.getBoundingClientRect().right;
            const fallbackRight = menu.offsetWidth > 0 ? menu.offsetLeft + menu.offsetWidth : 0;
            const right = rectRight > 0 ? rectRight : fallbackRight;
            const ready = right > 20; // avoid 0-width / not-yet-laid-out

            return { menu, right, ready };
        };

        const positionVRButton = () => {
            const { right } = computeMenuRight();
            const left = Math.max(8, Math.round(right + 8));

            element.style.position = 'fixed';
            element.style.top = '8px';
            element.style.left = `${left}px`;
            element.style.bottom = '';
            element.style.right = '';
        };

        // Custom menu DOM is injected async; retry until it exists & has layout.
        let rafTries = 0;
        const tryPositionUntilReady = () => {
            rafTries++;
            positionVRButton();

            const { menu, ready } = computeMenuRight();
            if (ready) {
                if (menu && 'ResizeObserver' in window) {
                    sidebarResizeObserver = new ResizeObserver(() => positionVRButton());
                    sidebarResizeObserver.observe(menu);
                }
                return;
            }

            if (rafTries < 240) {
                requestAnimationFrame(tryPositionUntilReady);
            }
        };

        requestAnimationFrame(tryPositionUntilReady);
        window.addEventListener('resize', positionVRButton);

        element.style.margin = '4px';
        element.style.fontSize = '100%';
        element.style.width = '2.5em';
        element.style.height = '2.5em';
        element.style.padding = '0';
        element.style.textShadow = 'black 2px 2px 2px';
        element.style.display = 'block';

        elButtons.append(element);

        vrButton.onStart(() => viewer.dispatchEvent?.({ type: 'vr_start' }));
        vrButton.onEnd(() => viewer.dispatchEvent?.({ type: 'vr_end' }));

        // Expose a cleanup hook so apps can unbind listeners/observers when the viewer is destroyed.
        vrButtonCleanup = () => {
            window.removeEventListener('resize', positionVRButton);
            if (sidebarResizeObserver) {
                sidebarResizeObserver.disconnect();
                sidebarResizeObserver = null;
            }
        };
    } catch (err) {
        console.warn('VR button init failed:', err);
    }
}

// Call this when tearing down the Potree viewer to release VR-related listeners and observers.
export function disposeVRButton() {
    if (vrButtonCleanup) {
        vrButtonCleanup();
        vrButtonCleanup = null;
    }
}

function initOSMComponents(viewerDomElement: HTMLElement, params: PootreRenderPreparationsParameters) {
    const {
        projection = '',
        // coordinate_X,
        // coordinate_Y,
        // coordinate_Z,
    } = params;
    window.viewer = new Potree.Viewer(viewerDomElement, {
        useDefaultRenderLoop: false,
    });
    window.viewer.toggleNavigationCube();
    window.viewer.toggleCompass();
    window.viewer.setBackground('null'); // clear background

    // show osm attribution

    const osmAttribution = document.getElementById(OSM_ATTRIBUTION_ID);
    if (osmAttribution) {
        osmAttribution.style.display = 'block';
    }

    window.cesiumViewer = new Cesium.Viewer('cesiumContainer', {
        useDefaultRenderLoop: false,
        animation: false,
        baseLayerPicker: false,
        fullscreenButton: false,
        geocoder: false,
        homeButton: false,
        infoBox: false,
        sceneModePicker: false,
        selectionIndicator: false,
        timeline: false,
        navigationHelpButton: false,
        imageryProvider: Cesium.createOpenStreetMapImageryProvider({
            url: OPEN_STREET_MAP_URL,
        }),
        terrainShadows: Cesium.ShadowMode.DISABLED,
    });

    // 3d coordinate
    const cp = new Cesium.Cartesian3();

    // set camera view to coordinate
    cesiumViewer.camera.setView({
        destination: cp,
        orientation: {
            heading: 10,
            pitch: -Cesium.Math.PI_OVER_TWO * 0.5,
            roll: 0.0,
        },
    });

    if (projection) {
        const pointcloudProjection = projection;
        const mapProjection = proj4.defs(OPEN_STREET_MAP_PROJECTION); // set coordinate system

        // create proj4 instances
        window.toMap = proj4(pointcloudProjection, mapProjection);
        window.toScene = proj4(mapProjection, pointcloudProjection);
        setOsmProjection(projection);
    }

    function loop(timestamp: any) {
        requestAnimationFrame(loop);

        viewer.update(viewer.clock.getDelta(), timestamp);

        viewer.render();

        if (window.toMap !== undefined) {
            {
                const camera = viewer.scene.getActiveCamera(); // get camera instances

                const pPos = new THREE.Vector3(0, 0, 0).applyMatrix4(camera.matrixWorld);

                const pUp = new THREE.Vector3(0, 600, 0).applyMatrix4(camera.matrixWorld);
                const pTarget = viewer.scene.view.getPivot();

                const toCes = (pos: any) => {
                    const xy = [pos.x, pos.y];
                    const height = pos.z;
                    const deg = toMap.forward(xy); // from potree projection to map projection, result is [long, lat]
                    const cPos = Cesium.Cartesian3.fromDegrees(deg[0], deg[1], height);

                    return cPos;
                };

                const cPos = toCes(pPos);
                const cUpTarget = toCes(pUp);
                const cTarget = toCes(pTarget);

                let cDir = Cesium.Cartesian3.subtract(cTarget, cPos, new Cesium.Cartesian3());
                let cUp = Cesium.Cartesian3.subtract(cUpTarget, cPos, new Cesium.Cartesian3());

                cDir = Cesium.Cartesian3.normalize(cDir, new Cesium.Cartesian3());
                cUp = Cesium.Cartesian3.normalize(cUp, new Cesium.Cartesian3());

                // debugger;

                cesiumViewer.camera.setView({
                    destination: cPos,
                    orientation: {
                        direction: cDir,
                        up: cUp,
                    },
                });
            }

            const aspect = viewer.scene.getActiveCamera().aspect;
            if (aspect < 1) {
                const fovy = Math.PI * (viewer.scene.getActiveCamera().fov / 180);
                cesiumViewer.camera.frustum.fov = fovy;
            } else {
                const fovy = Math.PI * (viewer.scene.getActiveCamera().fov / 180);
                const fovx = Math.atan(Math.tan(0.5 * fovy) * aspect) * 2;
                cesiumViewer.camera.frustum.fov = fovx;
            }

            cesiumViewer.render();
        }
    }

    requestAnimationFrame(loop);
}
